/* Модель экрана «Говорящий кружок» (APP-SPEC-4): кто говорит, чем говорит, формат, чего не хватает, название.
   Ключи заказа — только опубликованные формой (fields[] и screen_fields[], tool-form.ts): старый набор шаблона
   (source, cast, photo, video, audio, voice_id, audio_voice_id, visualization, movement…) и набор контракта экрана
   03-api.md §9.2 (who, avatar_id, me, person_id, photo_upload_id, crop, trim, speech, occasion, voice_mode, viz_style,
   motion, sfx_prompt, loop). Чего форма не публикует, экран не пишет. */
import type { StudioForm } from '../studio-api';
import { uiFormat } from '../../../i18n';
import { policyFlag, toolField, type ToolField } from './tool-form';
export type CircleField = StudioForm['fields'][number];
export interface CirclePolicy {
  policy_version: 'circle-v1' | 'circle-n8n-v1';
  face_fields: string[];
  speech_fields: string[];
  exactly_one_face: boolean;
  exactly_one_speech: boolean;
  text_voice_field: string;
  audio_voice_field: string;
  default_format: string;
  max_duration_ms: number;
  video_revoice?: boolean;
  text_min_length?: number;
  circle_text_max_length?: number;
  video_text_max_length?: number;
  sfx?: boolean;
  sfx_sources?: string[];
  sfx_max_length?: number | null;
  loop?: boolean;
  own_video_new_words?: boolean;
  video_speech_fields?: string[];
  video_original_speech?: boolean;
  audio_revoice?: boolean;
  visualization?: boolean;
  video_auto_loop?: boolean;
}
export function circlePolicy(form: StudioForm | null | undefined): CirclePolicy | null {
  const value = (form?.constraints as unknown as { circle?: CirclePolicy } | undefined)?.circle;
  return value && ['circle-v1', 'circle-n8n-v1'].includes(value.policy_version) ? value : null;
}
export function circleVideoSpeechFields(form: StudioForm) {
  const policy = circlePolicy(form);
  return policy?.own_video_new_words === true
    ? (policy.video_speech_fields ?? []).filter(key => ['text', 'audio'].includes(key) && form.fields.some(field => field.key === key))
    : [];
}
export function circleAudioRevoice(policy: CirclePolicy) {
  return policy.audio_revoice ?? policy.policy_version === 'circle-v1';
}

/* ── Ключи формы ── */
export interface CircleKeys {
  who?: string;
  source?: string;
  cast?: string;
  avatar?: string;
  me?: string;
  person?: string;
  photo?: string;
  video?: string;
  audio?: string;
  text?: string;
  occasion?: string;
  speech?: string;
  voiceMode?: string;
  textVoice?: string;
  audioVoice?: string;
  format?: string;
  skin?: string;
  motion?: string;
  movement?: string;
  negative?: string;
  viz?: string;
  sfx?: string;
  loop?: string;
  crop?: string;
  cropX?: string;
  cropY?: string;
  zoom?: string;
  trim?: string;
  start?: string;
  end?: string;
}
export function circleKeys(form: StudioForm | null | undefined): CircleKeys {
  const policy = circlePolicy(form);
  const f = (...keys: string[]) => toolField(form, ...keys)?.key;
  const voiceMode = f('voice_mode');
  const cropX = f('crop_x'),
    cropY = f('crop_y'),
    zoom = f('zoom'),
    start = f('start_ms'),
    end = f('end_ms');
  return {
    who: f('who'),
    source: f('source'),
    cast: toolField(form, 'cast')?.type === 'cast' ? 'cast' : undefined,
    avatar: f('avatar_id'),
    me: f('me'),
    person: f('person_id'),
    photo: f('photo_upload_id', 'photo'),
    video: f('video_upload_id', 'video'),
    audio: f('audio_upload_id', 'audio'),
    text: f('text'),
    occasion: f('occasion'),
    speech: f('speech'),
    voiceMode,
    textVoice: f(policy?.text_voice_field ?? 'voice_id', 'voice_id'),
    audioVoice: voiceMode ? f('voice_id') : f(policy?.audio_voice_field ?? 'audio_voice_id', 'audio_voice_id'),
    format: f('format'),
    skin: f('skin_id'),
    motion: f('motion'),
    movement: f('movement'),
    negative: f('negative_movement'),
    viz: f('viz_style', 'visualization'),
    sfx: f('sfx_prompt'),
    loop: f('loop'),
    crop: f('crop'),
    ...(cropX && cropY && zoom ? { cropX, cropY, zoom } : {}),
    trim: f('trim'),
    ...(start && end ? { start, end } : {}),
  };
}
/** Все ключи входа «кто говорит» и речи: при смене группы источника экран очищает их до нового выбора. */
export function circleResetKeys(keys: CircleKeys, policy: CirclePolicy | null) {
  return [
    ...(policy?.face_fields ?? []),
    ...(policy?.speech_fields ?? []),
    policy?.text_voice_field,
    policy?.audio_voice_field,
    keys.cast,
    keys.avatar,
    keys.me,
    keys.person,
    keys.photo,
    keys.video,
    keys.audio,
    keys.text,
    keys.occasion,
    keys.speech,
    keys.voiceMode,
    keys.textVoice,
    keys.audioVoice,
    keys.skin,
    keys.motion,
    keys.movement,
    keys.negative,
    keys.viz,
    keys.sfx,
    keys.loop,
    keys.crop,
    keys.cropX,
    keys.cropY,
    keys.zoom,
    keys.trim,
    keys.start,
    keys.end,
    /* имена прежних полей — даже если форма их сейчас не публикует (восстановленный черновик) */
    'skin_id',
    'movement',
    'negative_movement',
    'sfx_prompt',
    'visualization',
    'start_ms',
    'end_ms',
    'crop_x',
    'crop_y',
    'zoom',
  ].filter((key, index, list): key is string => !!key && list.indexOf(key) === index);
}

/* ── Кто говорит ── */
export type CircleWho = 'char' | 'me' | 'person' | 'photo' | 'video' | 'audio';
export type CircleGroup = 'face' | 'video' | 'audio';
export const circleGroup = (who: CircleWho | null): CircleGroup => (who === 'video' ? 'video' : who === 'audio' ? 'audio' : 'face');
/** Какие варианты «кто говорит» публикует форма. */
export function circleSources(form: StudioForm | null | undefined) {
  const keys = circleKeys(form);
  const policy = circlePolicy(form);
  const whoField = keys.who ? toolField(form, keys.who) : undefined;
  const sourceField = keys.source ? toolField(form, keys.source) : undefined;
  const castKinds = (keys.cast ? (toolField(form, keys.cast) as ToolField & { reference_kinds?: string[] | null }).reference_kinds : null) ?? [];
  const allowed = (who: CircleWho) => (whoField?.values.length ? whoField.values.includes(who) : true);
  const group = (value: CircleGroup) =>
    sourceField ? sourceField.values.includes(value) && (value !== 'audio' || policy?.policy_version === 'circle-v1' || policy?.visualization === true) : value === 'face' || !!whoField;
  const kind = (prefix: string) => !castKinds.length || castKinds.some(item => item.startsWith(prefix));
  return {
    char: allowed('char') && group('face') && (!!keys.avatar || (!!keys.cast && kind('character:'))),
    me: allowed('me') && group('face') && (!!keys.me || (!!keys.cast && (kind('self') || kind('user_avatar:')))),
    person: allowed('person') && group('face') && (!!keys.person || (!!keys.cast && kind('person:'))),
    photo: allowed('photo') && group('face') && !!keys.photo,
    video: allowed('video') && group('video') && !!keys.video,
    audio: allowed('audio') && group('audio') && !!(keys.audio || keys.text),
  };
}
/** Кто говорит сейчас: по входам формы, «Фото»/«Видео» без файла — по выбору в ряду (pick). */
export function circleWho(form: StudioForm | null | undefined, inputs: Record<string, string>, pick?: CircleWho | null): CircleWho | null {
  const keys = circleKeys(form);
  if (keys.who && ['char', 'me', 'person', 'photo', 'video', 'audio'].includes(inputs[keys.who])) return inputs[keys.who] as CircleWho;
  const source = keys.source ? inputs[keys.source] || 'face' : 'face';
  if (source === 'audio') return 'audio';
  if (source === 'video') return 'video';
  if (pick === 'photo' || (keys.photo && inputs[keys.photo])) return 'photo';
  const cast = keys.cast ? inputs[keys.cast] ?? '' : '';
  if (cast === 'self' || cast.startsWith('user_avatar:')) return 'me';
  if (cast.startsWith('person:')) return 'person';
  if (cast.startsWith('character:')) return 'char';
  if (keys.avatar && inputs[keys.avatar]) return 'char';
  if (keys.person && inputs[keys.person]) return 'person';
  if (keys.me && inputs[keys.me]) return 'me';
  return pick === 'video' || pick === 'audio' ? pick : null;
}
/** Персонаж, близкий или вариант «Вы» в форме ряда лиц: `char:<id>`, `person:<id>`, `me`. */
export function circleFace(form: StudioForm | null | undefined, inputs: Record<string, string>): string {
  const keys = circleKeys(form);
  const cast = keys.cast ? inputs[keys.cast] ?? '' : '';
  if (cast.startsWith('character:')) return `char:${cast.slice(10)}`;
  if (cast.startsWith('person:')) return cast;
  if (cast === 'self' || cast.startsWith('user_avatar:')) return 'me';
  if (keys.avatar && inputs[keys.avatar]) return `char:${inputs[keys.avatar]}`;
  if (keys.person && inputs[keys.person]) return `person:${inputs[keys.person]}`;
  if (keys.me && inputs[keys.me]) return 'me';
  return '';
}
export function circleCharacterId(form: StudioForm | null | undefined, inputs: Record<string, string>): number | null {
  const face = circleFace(form, inputs);
  const id = face.startsWith('char:') ? Number(face.slice(5)) : NaN;
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

/* ── Чем говорит ── */
export type CircleSpeech = 'text' | 'audio' | 'own';
/** Своя речь ролика возможна (форма не запрещает исходную речь). */
export function circleOwnAllowed(form: StudioForm | null | undefined) {
  const policy = circlePolicy(form);
  const keys = circleKeys(form);
  if (keys.speech) return toolField(form, keys.speech)!.values.length ? toolField(form, keys.speech)!.values.includes('own') : true;
  return policy?.video_original_speech !== false;
}
/** Чем говорит сейчас: выбор экрана, иначе по входам. */
export function circleSpeech(form: StudioForm | null | undefined, inputs: Record<string, string>, who: CircleWho | null, chosen?: CircleSpeech): CircleSpeech {
  const keys = circleKeys(form);
  const policy = circlePolicy(form);
  if (chosen) return chosen;
  const speechValue = keys.speech ? inputs[keys.speech] : '';
  if (speechValue === 'text' || speechValue === 'audio' || speechValue === 'own') return speechValue;
  if (keys.audio && inputs[keys.audio]) return 'audio';
  if (keys.text && inputs[keys.text]) return 'text';
  if (who === 'video') {
    if (circleOwnAllowed(form)) return 'own';
    const video = form ? circleVideoSpeechFields(form) : [];
    return video[0] === 'audio' ? 'audio' : 'text';
  }
  if (who === 'audio') return keys.audio ? 'audio' : 'text';
  if (policy?.speech_fields?.[0] === 'audio' && keys.audio) return 'audio';
  return keys.text ? 'text' : keys.audio ? 'audio' : 'text';
}
/** Новые слова у своего видео (текст или запись), если форма их публикует. */
export function circleNewWords(form: StudioForm | null | undefined) {
  if (!form) return [] as Array<'text' | 'audio'>;
  const keys = circleKeys(form);
  if (keys.speech) {
    const values = toolField(form, keys.speech)!.values;
    return (['text', 'audio'] as const).filter(value => (!values.length || values.includes(value)) && (value === 'text' ? !!keys.text : !!keys.audio));
  }
  return circleVideoSpeechFields(form) as Array<'text' | 'audio'>;
}

/* ── Формат ── */
export const CIRCLE_FORMATS: Array<[string, string, string]> = [
  ['circle', 'Кружок', 'Видеосообщение Telegram, до минуты'],
  ['9:16', 'Сторис', 'Для Reels, Shorts и сторис'],
  ['16:9', 'Горизонтальное', 'Для YouTube и экрана'],
  ['1:1', 'Квадрат', 'Для ленты'],
  ['4:5', 'Пост', 'Для ленты'],
];
export function circleFormat(form: StudioForm | null | undefined, inputs: Record<string, string>) {
  const keys = circleKeys(form);
  return (keys.format && inputs[keys.format]) || circlePolicy(form)?.default_format || 'circle';
}
/** Значения формата в листе: опубликованные; «Пост» — только когда уже выбран (старый вход, Q-410). */
export function circleFormatChoices(form: StudioForm | null | undefined, current: string) {
  const keys = circleKeys(form);
  const values = keys.format ? toolField(form, keys.format)!.values : [];
  return CIRCLE_FORMATS.filter(([value]) => values.includes(value) && (value !== '4:5' || current === '4:5'));
}
export const circleFormatWord = (value: string) => (value === '16:9' ? 'Горизонт.' : (CIRCLE_FORMATS.find(item => item[0] === value)?.[1] ?? value));
export function circleRatio(format: string) {
  if (format === 'circle') return 1;
  const [w, h] = format.split(':').map(Number);
  return w > 0 && h > 0 ? w / h : 9 / 16;
}
/** Рамка главной сцены (frameWH прототипа): круг 184 (широкий 212), видео — в коробке 220×248 (280×300), горизонталь — до 320 (380). */
export function circleFrame(format: string, wide = false) {
  if (format === 'circle') {
    const size = wide ? 212 : 184;
    return { w: size, h: size };
  }
  const ratio = circleRatio(format);
  if (ratio > 1.3) {
    const w = wide ? 380 : 320;
    return { w, h: Math.round(w / ratio) };
  }
  const box = wide ? { w: 280, h: 300 } : { w: 220, h: 248 };
  return ratio >= box.w / box.h ? { w: box.w, h: Math.round(box.w / ratio) } : { w: Math.round(box.h * ratio), h: box.h };
}

/* ── Текст ── */
export function circleTextLimit(form: StudioForm, field: CircleField, inputs: Record<string, string>) {
  const policy = circlePolicy(form);
  const configured =
    (inputs.format || policy?.default_format || 'circle') === 'circle' ? policy?.circle_text_max_length : policy?.video_text_max_length;
  return Math.min(field.max_length ?? 250, typeof configured === 'number' && configured > 0 ? configured : (field.max_length ?? 250));
}
export function circleTextBounds(form: StudioForm | null | undefined, inputs: Record<string, string>) {
  const keys = circleKeys(form);
  const field = keys.text ? toolField(form, keys.text) : undefined;
  const policy = circlePolicy(form);
  const max = form && field ? circleTextLimit(form, field, { ...inputs, format: circleFormat(form, inputs) }) : 250;
  return { min: policy?.text_min_length ?? 0, max };
}
/** Подсказка поля по тому, кто говорит (phMsg прототипа). */
export function circlePlaceholder(who: CircleWho | null, name: string) {
  const eg = 'Например: привет, с понедельником, пусть неделя будет лёгкой';
  if (who === 'audio') return `Что прозвучит? ${eg}`;
  if (who === 'me') return `Что вы скажете? ${eg}`;
  if ((who === 'char' || who === 'person') && name) return `Что скажет ${name}? ${eg}`;
  if (who === 'photo' || who === 'video') return `Что скажет человек в кадре? ${eg}`;
  return `Что скажет герой? ${eg}`;
}
/** Поводы (OCC прототипа): подпись → готовый текст. Показываются только при поле occasion; значения — опубликованные. */
export const CIRCLE_OCCASIONS: Array<[string, string]> = [
  ['С днём рождения', 'С днём рождения. Пусть этот год будет лёгким, а мечты сбываются одна за другой'],
  ['С понедельником', 'Привет. С понедельником, пусть неделя будет лёгкой и спокойной'],
  ['Доброе утро', 'Доброе утро. Пусть день будет тёплым, а все дела складываются сами'],
  ['Новый год', 'С Новым годом. Пусть он принесёт больше счастливых дней, тёплых встреч и исполненных желаний'],
  ['8 Марта', 'С 8 Марта. Пусть весна принесёт вдохновение, а каждый день будет полон света и нежности'],
  ['23 Февраля', 'С 23 Февраля. Желаю силы, спокойной уверенности и надёжных друзей рядом'],
  ['Свадьба', 'Поздравляю со свадьбой. Пусть в вашем доме всегда будут любовь, смех и поддержка'],
  ['Просто так', 'Привет. Просто хочу напомнить: ты замечательный человек, и рядом с тобой мир становится лучше'],
];
export function circleOccasions(form: StudioForm | null | undefined) {
  const keys = circleKeys(form);
  if (!keys.occasion) return [];
  const values = toolField(form, keys.occasion)!.values;
  return CIRCLE_OCCASIONS.filter(([label]) => !values.length || values.includes(label));
}
/** Пресеты движения (MV_DO, MV_NOT): тексты интерфейса чипов, ставятся в поле целиком. */
export const CIRCLE_MOTION_DO: Array<[string, string]> = [
  ['Моргает и улыбается', 'Человек моргает, слегка улыбается и едва заметно кивает'],
  ['Поворот головы', 'Медленно поворачивает голову к камере и смотрит в объектив'],
  ['Ветер в волосах', 'Лёгкий ветер шевелит волосы, свет мягко меняется'],
  ['Наезд камеры', 'Камера медленно наезжает, фон уходит в лёгкую нерезкость'],
  ['Смеётся', 'Смеётся, чуть откидывает голову, плечи подрагивают'],
];
export const CIRCLE_MOTION_NOT: Array<[string, string]> = [
  ['Без лишних рук', 'Руки не появляются в кадре'],
  ['Без искажений', 'Лицо не искажается, пропорции не меняются'],
  ['Фон на месте', 'Фон не двигается и не меняется'],
  ['Без надписей', 'Нет надписей и водяных знаков'],
];

/* ── Анимация звука («Кружок из аудио», §6.1) ── */
export const CIRCLE_VIZ: Array<[string, string, string]> = [
  ['orb', 'Сфера', 'Мягкое свечение, как в голосовом режиме'],
  ['morph', 'Морф', 'Переливающаяся капля меняет форму'],
  ['ferro', 'Феррожидкость', 'Чёрная магнитная жидкость с шипами'],
  ['wave', 'Волна', 'Плавная лента звука'],
  ['ring', 'Кольцо', 'Бесконечное зеркало со светодиодами'],
  ['pulse', 'Пульс', 'Расходящиеся круги'],
  ['retro', 'Ретро', 'Осциллограф на зелёном экране'],
  ['matrix', 'Матрица', 'Падающие символы'],
  ['bars', 'Полосы', 'Столбики уровня звука'],
];
/** Стили, которые публикует форма (только опубликованные значения поля viz_style / visualization). */
export function circleVizChoices(form: StudioForm | null | undefined) {
  const keys = circleKeys(form);
  const values = keys.viz ? toolField(form, keys.viz)!.values : [];
  return values.map(value => CIRCLE_VIZ.find(item => item[0] === value) ?? ([value, value, ''] as [string, string, string]));
}

/* ── Необязательные блоки по флагам ── */
/** «Фоновый звук» (§5.3): поле опубликовано и политика его не выключает (sfx или sfx_sources по группе источника). */
export function circleSfxOn(form: StudioForm | null | undefined, who: CircleWho | null, speech: CircleSpeech) {
  const keys = circleKeys(form);
  const policy = circlePolicy(form);
  if (!keys.sfx || who === 'audio' || speech === 'own') return false;
  if (!policy) return true;
  if (policy.sfx === false) return false;
  if (policy.sfx === true) return true;
  return (policy.sfx_sources ?? []).includes(circleGroup(who));
}
/** «Зациклить» (§5.3): своё видео с новыми словами, флаг loop политики и поле loop. */
export function circleLoopOn(form: StudioForm | null | undefined, who: CircleWho | null, speech: CircleSpeech) {
  const keys = circleKeys(form);
  return who === 'video' && speech !== 'own' && !!keys.loop && policyFlag(circlePolicy(form) as unknown as Record<string, unknown>, 'loop');
}
/** «Движение» (§5.3): своё фото, «Вы», близкий. */
export function circleMotionOn(form: StudioForm | null | undefined, who: CircleWho | null) {
  const keys = circleKeys(form);
  return ['photo', 'me', 'person'].includes(who ?? '') && !!(keys.motion || keys.movement || keys.negative);
}
export function circleMotionValue(form: StudioForm | null | undefined, inputs: Record<string, string>) {
  const keys = circleKeys(form);
  if (keys.motion) {
    try {
      const value = JSON.parse(inputs[keys.motion] || '{}') as { do?: unknown; not?: unknown };
      return { do: typeof value.do === 'string' ? value.do : '', not: typeof value.not === 'string' ? value.not : '' };
    } catch {
      return { do: '', not: '' };
    }
  }
  return { do: keys.movement ? inputs[keys.movement] ?? '' : '', not: keys.negative ? inputs[keys.negative] ?? '' : '' };
}
/** Запись движения: motion {do, not} JSON-строкой или два поля шаблона. */
export function circleMotionWrite(form: StudioForm | null | undefined, inputs: Record<string, string>, value: { do: string; not: string }) {
  const keys = circleKeys(form);
  const next = { ...inputs };
  if (keys.motion) {
    if (value.do.trim() || value.not.trim()) next[keys.motion] = JSON.stringify({ do: value.do, not: value.not });
    else delete next[keys.motion];
    return next;
  }
  for (const [key, text] of [
    [keys.movement, value.do],
    [keys.negative, value.not],
  ] as const) {
    if (!key) continue;
    if (text) next[key] = text;
    else delete next[key];
  }
  return next;
}

/* ── Кадр и фрагмент ── */
export interface CircleCrop {
  cx: number;
  cy: number;
  z: number;
}
export function circleCropValue(form: StudioForm | null | undefined, inputs: Record<string, string>): CircleCrop | null {
  const keys = circleKeys(form);
  if (keys.crop && inputs[keys.crop]) {
    try {
      const value = JSON.parse(inputs[keys.crop]) as Record<string, unknown>;
      const cx = Number(value.cx),
        cy = Number(value.cy),
        z = Number(value.z);
      if ([cx, cy, z].every(Number.isFinite)) return { cx, cy, z };
    } catch {
      return null;
    }
  }
  if (keys.cropX && inputs[keys.cropX] !== undefined && inputs[keys.cropY!] !== undefined) {
    const cx = Number(inputs[keys.cropX]),
      cy = Number(inputs[keys.cropY!]),
      z = Number(inputs[keys.zoom!] ?? 1);
    if ([cx, cy, z].every(Number.isFinite)) return { cx, cy, z };
  }
  return null;
}
export const circleCropAvailable = (form: StudioForm | null | undefined) => {
  const keys = circleKeys(form);
  return !!(keys.crop || keys.cropX);
};
export function circleCropWrite(form: StudioForm | null | undefined, inputs: Record<string, string>, crop: CircleCrop | null) {
  const keys = circleKeys(form);
  const next = { ...inputs };
  const round = (value: number, digits: number) => String(Number(value.toFixed(digits)));
  if (keys.crop) {
    if (crop) next[keys.crop] = JSON.stringify({ cx: Number(crop.cx.toFixed(4)), cy: Number(crop.cy.toFixed(4)), z: Number(crop.z.toFixed(3)) });
    else delete next[keys.crop];
  } else if (keys.cropX && keys.cropY && keys.zoom) {
    if (crop) {
      next[keys.cropX] = round(crop.cx, 4);
      next[keys.cropY] = round(crop.cy, 4);
      next[keys.zoom] = round(crop.z, 3);
    } else for (const key of [keys.cropX, keys.cropY, keys.zoom]) delete next[key];
  }
  return next;
}
export function circleTrimValue(form: StudioForm | null | undefined, inputs: Record<string, string>): { start: number; end: number } | null {
  const keys = circleKeys(form);
  if (keys.trim && inputs[keys.trim]) {
    try {
      const value = JSON.parse(inputs[keys.trim]) as Record<string, unknown>;
      const start = Number(value.start_ms),
        end = Number(value.end_ms);
      if (Number.isFinite(start) && Number.isFinite(end) && end > start) return { start, end };
    } catch {
      return null;
    }
  }
  if (keys.start && inputs[keys.start] !== undefined && inputs[keys.end!] !== undefined) {
    const start = Number(inputs[keys.start]),
      end = Number(inputs[keys.end!]);
    if (Number.isFinite(start) && Number.isFinite(end) && end > start) return { start, end };
  }
  return null;
}
export const circleTrimAvailable = (form: StudioForm | null | undefined) => {
  const keys = circleKeys(form);
  return !!(keys.trim || keys.start);
};
export function circleTrimWrite(form: StudioForm | null | undefined, inputs: Record<string, string>, trim: { start: number; end: number } | null) {
  const keys = circleKeys(form);
  const next = { ...inputs };
  const start = trim ? Math.max(0, Math.round(trim.start / 100) * 100) : 0;
  const end = trim ? Math.max(start + 100, Math.round(trim.end / 100) * 100) : 0;
  if (keys.trim) {
    if (trim) next[keys.trim] = JSON.stringify({ start_ms: start, end_ms: end });
    else delete next[keys.trim];
  } else if (keys.start && keys.end) {
    if (trim) {
      next[keys.start] = String(start);
      next[keys.end] = String(end);
    } else {
      delete next[keys.start];
      delete next[keys.end];
    }
  }
  return next;
}

/* ── Название (titleOf / circTitle) ── */
export interface CirclePreset {
  /** «Кружок из аудио» */
  audio?: boolean;
  /** «Видеооткрытка» */
  card?: boolean;
  /** Шаблон формата «кружок»: его название, формат закреплён */
  template?: string | null;
}
export function circleTitle(preset: CirclePreset, format: string, who: CircleWho | null) {
  const round = format === 'circle' || !!preset.template;
  if (preset.template) return preset.template;
  if (preset.audio || who === 'audio') return round ? 'Кружок из аудио' : 'Видео из аудио';
  if (preset.card) return 'Видеооткрытка';
  if (round) return 'Говорящий кружок';
  if (who === 'video') return 'Видео говорит';
  if (who === 'photo') return 'Фото говорит';
  return 'Говорящее видео';
}

/* ── Совместимые помощники прежней формы ── */
/** Разделы меняют только представление опубликованной формы, ключи и значения остаются серверными. */
export function circleSection(field: CircleField): 'speaker' | 'speech' | 'settings' {
  if (field.upload?.kind === 'photo' || field.upload?.kind === 'video' || field.type === 'cast') return 'speaker';
  if (field.upload?.kind === 'audio' || field.key === 'text') return 'speech';
  return 'settings';
}
export function circleLabel(key: string) {
  return (
    (
      {
        photo: 'Фото с лицом',
        photo_upload_id: 'Фото с лицом',
        video: 'Видео',
        video_upload_id: 'Видео',
        audio: 'Речевая запись',
        audio_upload_id: 'Речевая запись',
        text: 'Что скажет?',
        voice_id: 'Голос',
        audio_voice_id: 'Другой голос для записи',
        cast: 'Кто говорит',
        format: 'Формат',
        ratio: 'Формат',
        who: 'Кто говорит',
        style: 'Стиль',
        sfx: 'Фоновый звук',
        sfx_prompt: 'Фоновый звук',
        motion: 'Движение',
        movement: 'Движение',
        negative_movement: 'Каких движений избегать',
        skin_id: 'Вид персонажа',
        source: 'Источник',
        start_ms: 'Начало фрагмента, секунды',
        end_ms: 'Конец фрагмента, секунды',
        crop_x: 'Кадр по горизонтали',
        crop_y: 'Кадр по вертикали',
        zoom: 'Приближение',
        visualization: 'Анимация звука',
        viz_style: 'Стиль анимации',
      } as Record<string, string>
    )[key] ?? 'Дополнительная настройка'
  );
}
export function circleValueLabel(value: string) {
  return (
    (
      {
        circle: 'Кружок',
        '9:16': 'Сторис',
        '16:9': 'Горизонтальное',
        '1:1': 'Квадрат',
        '4:5': 'Пост',
        face: 'Человек или персонаж',
        video: 'Своё видео',
        audio: 'Анимация звука',
        ...Object.fromEntries(CIRCLE_VIZ.map(([id, name]) => [id, name])),
      } as Record<string, string>
    )[value] ?? value
  );
}

/* ── Чего не хватает (missing, §7.1) ── */
export interface CircleMissingContext {
  /** «Фото»/«Видео» в ряду без файла */
  pick?: CircleWho | null;
  /** Идёт кадрирование */
  cropping?: boolean;
  /** Выбранный близкий без фото: его имя */
  personWithoutPhoto?: string | null;
  /** Выбран «Вы» без своих фото и варианта */
  meWithoutPhoto?: boolean;
}
/** selectedSpeech: выбор экрана (`text` · `audio` · `own`; прежнее `original` = `own`). */
export function circleMissing(
  form: StudioForm,
  inputs: Record<string, string>,
  rights: boolean,
  selectedSpeech?: string,
  context: CircleMissingContext = {},
): string | null {
  const policy = circlePolicy(form);
  const keys = circleKeys(form);
  const structured = !!(policy || keys.who);
  if (structured) {
    if (context.cropping) return 'Выберите кадр';
    const who = circleWho(form, inputs, context.pick);
    if (!who) return 'Выберите, кто говорит';
    if (who === 'person' && context.personWithoutPhoto) return uiFormat('Добавьте фото: {0}', context.personWithoutPhoto);
    if (who === 'me' && context.meWithoutPhoto) return 'Добавьте свои фото';
    if (circleGroup(who) === 'face' && policy?.exactly_one_face) {
      const faces = policy.face_fields.filter(key => !!inputs[key]?.trim()).length;
      if (faces > 1) return 'Выберите, кто говорит';
    }
    if (who === 'photo' && !(keys.photo && inputs[keys.photo]?.trim())) return 'Загрузите фото с лицом';
    if (who === 'video' && !(keys.video && inputs[keys.video]?.trim())) return 'Загрузите видео';
    const chosen = selectedSpeech === 'original' ? 'own' : (selectedSpeech as CircleSpeech | undefined);
    const speech = circleSpeech(form, inputs, who, chosen);
    if (speech === 'own') {
      if (who !== 'video' || !circleOwnAllowed(form)) return 'Напишите, что скажет';
      if ((keys.text && inputs[keys.text]?.trim()) || (keys.audio && inputs[keys.audio]?.trim())) return 'Напишите, что скажет';
    } else {
      if (who === 'video' && !circleNewWords(form).includes(speech)) return 'Напишите, что скажет';
      const hasText = !!(keys.text && inputs[keys.text]?.trim());
      const hasAudio = !!(keys.audio && inputs[keys.audio]?.trim());
      if (hasText && hasAudio && (policy?.exactly_one_speech ?? true)) return 'Оставьте текст или запись';
      if (speech === 'audio') {
        if (!hasAudio) return 'Запишите или загрузите речь';
      } else {
        const text = keys.text ? (inputs[keys.text] ?? '').trim() : '';
        const { min, max } = circleTextBounds(form, inputs);
        if (Array.from(text).length > max) return uiFormat('Не больше {0} знаков', max);
        if (!text) return 'Напишите, что скажет';
        if (min && Array.from(text).length < min) return uiFormat('Минимум {0} символов', min);
        const voiceKey = keys.textVoice;
        if (voiceKey && !inputs[voiceKey]?.trim()) return 'Выберите голос';
      }
    }
  }
  const required = form.fields.find(field => field.required && !inputs[field.key]?.trim());
  if (required) return circleLabel(required.key);
  if (form.fields.some(field => field.upload) && !rights && form.fields.some(field => field.upload && inputs[field.key])) return 'Подтвердите право использовать материалы';
  if (!structured && form.fields.some(field => field.upload) && !rights) return 'Подтвердите право использовать материалы';
  return null;
}
